// Dashboard.jsx (for monitoring dashboard)
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchData } from "../../../utils/functions";
import DashboardSearchFilters from "../../common/DashboardSearchFilters";

// Import monitoring components
import OverallStatusWidget from "./OverallStatusWidget";
import SummaryMetricsWidget from "./SummaryMetricsWidget";
import VisitMetricsWidget from "./VisitMetricsWidget";
import FunctionalStatusWidget from "./FunctionalStatusWidget";
import ConditionChart from "./ConditionChart";
import ProgressWidget from "./ProgressWidget";
import CommunityFeedbackWidget from "./CommunityFeedbackWidget";
import NoData, { hasData } from "../../common/NoData";

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];
  const canViewMonitoring = permissions.includes("dashboard-household");

  const [monitoringData, setMonitoringData] = useState({
    overallStatus: null,
    visitMetrics: null,
    functionalStatus: null,
    physicalCondition: null,
    physicalProgress: null,
    communityFeedback: null,
    monitoringByDistrict: null,
    monitoringByFeatureType: null,
  });

  // Filters state
  const [filters, setFilters] = useState({
    district_id: "",
    tehsil_id: "",
    uc_id: "",
    deh_id: "",
    village_id: "",
    dateField: "",
    condition: "",
    dateValue: "",
    startDate: "",
    endDate: "",
  });

  const [loading, setLoading] = useState({
    overallStatus: false,
    visitMetrics: false,
    functionalStatus: false,
    physicalCondition: false,
    physicalProgress: false,
    communityFeedback: false,
    monitoringByDistrict: false,
    monitoringByFeatureType: false,
  });
  useEffect(() => {
    fetchAllMonitoringData();
  }, [filters]);
  const fetchAllMonitoringData = async () => {
    if (!canViewMonitoring) {
      console.warn("User doesn't have permission to view monitoring dashboard");
      return;
    }
    setLoading({
      overallStatus: true,
      visitMetrics: true,
      functionalStatus: true,
      physicalCondition: true,
      physicalProgress: true,
      communityFeedback: true,
      monitoringByDistrict: true,
      monitoringByFeatureType: true,
    });

    try {
      const endpoints = [
        { key: "overallStatus", url: "monitoring-overall-status" },
        { key: "visitMetrics", url: "visit-metrics" },
        { key: "functionalStatus", url: "functional-status" },
        { key: "physicalCondition", url: "physical-condition" },
        { key: "physicalProgress", url: "physical-progress" },
        { key: "communityFeedback", url: "community-feedback" },
        { key: "monitoringByDistrict", url: "monitoring-by-district" },
        { key: "monitoringByFeatureType", url: "monitoring-by-feature-type" },
      ];
      const promises = endpoints.map(async ({ key, url }) => {
        try {
          const response = await fetchData(
            url,
            "GET",
            null,
            {},
            filters,
            navigate,
            "/monitoring-dashboard",
            dispatch
          );

          if (response.success) {
            setMonitoringData((prev) => ({
              ...prev,
              [key]: response.data || response.data?.data || {},
            }));
          } else {
            console.error(`Failed to fetch ${key}:`, response.message);
            setMonitoringData((prev) => ({
              ...prev,
              [key]: {},
            }));
          }
        } catch (error) {
          console.error(`Error fetching ${key}:`, error);
          setMonitoringData((prev) => ({
            ...prev,
            [key]: {},
          }));
        } finally {
          setLoading((prev) => ({
            ...prev,
            [key]: false,
          }));
        }
      });

      await Promise.all(promises);
    } catch (error) {
      console.error("Error fetching monitoring dashboard data:", error);
      Object.keys(loading).forEach((key) => {
        setLoading((prev) => ({ ...prev, [key]: false }));
      });
    }
  };

  const handleFilterChange = (appliedFilters) => {
    setFilters(appliedFilters);
  };

  const resetFilters = () => {
    setFilters({
      district_id: "",
      tehsil_id: "",
      uc_id: "",
      deh_id: "",
      village_id: "",
      dateField: "",
      condition: "",
      dateValue: "",
      startDate: "",
      endDate: "",
    });
  };
  const isLoading = Object.values(loading).some((status) => status);
  if (!canViewMonitoring) {
    return (
      <div className="w-full px-4 bg-gray-50">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center mt-4">
          <svg
            className="w-12 h-12 text-yellow-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-yellow-700 mb-2 text-balance">
            Access Denied
          </h3>
          <p className="text-yellow-600">
            You don't have permission to view the Monitoring Dashboard. Please
            contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 bg-gray-50">
      <div className="w-full flex flex-col justify-between mb-4">
        <DashboardSearchFilters
          accordionTitle="Monitoring Filters"
          defaultExpanded={false}
          fetchOnChange={false}
          apiRoutes={{
            districts: "districts",
            tehsils: "tehsils",
            ucs: "ucs",
            dehs: "dehs",
            villages: "villages",
          }}
          dateFields={[
            "created_at",
            "updated_at",
            "monitoring_date",
            "visit_date",
          ]}
          onApply={handleFilterChange}
        />
      </div>
      {isLoading && (
        <div className="text-center py-8">
          <div className="animate-spin h-8 w-8 border-b-2 border-teal-700 rounded-full mx-auto"></div>
          <p className="mt-2">Loading Monitoring Dashboard Data...</p>
        </div>
      )}
      {!isLoading && (() => {
        const has = {
          status: hasData(monitoringData.overallStatus, monitoringData.functionalStatus),
          visits: hasData(
            monitoringData.visitMetrics?.recentActivity,
            monitoringData.physicalCondition
          ),
          progress: hasData(
            monitoringData.physicalProgress?.physicalProgress,
            monitoringData.communityFeedback
          ),
        };
        return (
        <div className="bg-white border-dashed border-2 border-teal-700 min-h-40 w-full px-5 py-2">
          <div className="space-y-6">
            {!has.status && !has.visits && !has.progress && (
              <NoData className="py-16" />
            )}
            {has.status && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-6">
                <OverallStatusWidget
                  data={monitoringData.overallStatus}
                  loading={loading.overallStatus}
                />
              </div>
              {/* <div className="lg:col-span-3">
                <SummaryMetricsWidget
                  data={monitoringData.overallStatus}
                  loading={loading.overallStatus}
                /> */}
                {/* <FunctionalStatusWidget
                  apiData={{
                    success: true,
                    data: monitoringData.functionalStatus,
                  }}
                  loading={loading.functionalStatus}
                /> */}
              {/* </div> */}
              <div className="lg:col-span-6">
                {/* <SummaryMetricsWidget
                  data={monitoringData.overallStatus}
                  loading={loading.overallStatus}
                /> */}
                <FunctionalStatusWidget
                  apiData={{
                    success: true,
                    data: monitoringData.functionalStatus,
                  }}
                  loading={loading.functionalStatus}
                />
              </div>
            </div>
            )}
            {has.visits && (<>
            <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-8">
                <VisitMetricsWidget
                  data={monitoringData.visitMetrics}
                  loading={loading.visitMetrics}
                />
              </div>
              <div className="lg:col-span-4">
                <ConditionChart
                  data={monitoringData.physicalCondition}
                  loading={loading.physicalCondition}
                />
              </div>
            </div>
            </>)}
            {/* Horizontal Line */}
            {/* <hr className="border-t-2 border-dashed border-teal-700 my-4" /> */}

            {/* ROW 3: Functional Status */}
            {/* <div>
              <FunctionalStatusWidget
                apiData={{
                  success: true,
                  data: monitoringData.functionalStatus,
                }}
                loading={loading.functionalStatus}
              />
            </div> */}

            {has.progress && (<>
            <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ProgressWidget
                data={monitoringData.physicalProgress}
                loading={loading.physicalProgress}
              />
              <CommunityFeedbackWidget
                data={monitoringData.communityFeedback}
                loading={loading.communityFeedback}
              />
            </div>
            </>)}

            {/* Data Refresh Button */}
            {/* <div className="text-center pt-6 border-t border-gray-200">
              <button
                onClick={fetchAllMonitoringData}
                className="px-6 py-3 bg-[#137e81] text-white rounded-lg hover:bg-[#0f6b6d] transition-colors font-medium"
              >
                Refresh Dashboard Data
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Last updated: {new Date().toLocaleTimeString()}
              </p>
            </div> */}
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default Dashboard;
